import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    ArrowRight,
    Eye,
    EyeOff,
    LockKeyhole,
    Mail,
    ShieldCheck,
    Sparkles
} from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";
import { login, loginWithGoogle, saveSession } from "../services/authService";
import "./AuthPage.css";

function getErrorMessage(error, fallback = "Login failed. Please check your details and try again.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

export default function LoginPage() {
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const returnUrl = params.get("returnUrl");
    const isPublicEntry = params.get("entry") === "public";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    const pageCopy = useMemo(() => {
        if (isPublicEntry) {
            return {
                eyebrow: "Client Workspace",
                title: "Welcome back",
                subtitle: "Sign in to continue editing your generated website, CRM modules, leads, and live publishing workflow.",
                afterTitle: "After login",
                afterText: "You will land on your project workspace."
            };
        }

        return {
            eyebrow: "Admin Portal",
            title: "Welcome back",
            subtitle: "Access your Shriansh Nexus dashboard based on your role and workspace permissions.",
            afterTitle: "After login",
            afterText: "You will land on your role-based dashboard."
        };
    }, [isPublicEntry]);

    const redirectAfterLogin = (data) => {
        if (returnUrl) {
            window.location.href = returnUrl;
            return;
        }

        if (data?.isSuperAdmin) {
            window.location.href = "/";
        } else {
            window.location.href = "/projects";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim() || !password.trim()) {
            toast.error("Email and password are required.");
            return;
        }

        try {
            setLoading(true);

            const data = await login(email.trim(), password);
            saveSession(data);

            toast.success("Login successful. Opening workspace...");
            redirectAfterLogin(data);
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setGoogleLoading(true);

            const idToken = credentialResponse?.credential;

            if (!idToken) {
                toast.error("Google token was not received. Please try again.");
                return;
            }

            const data = await loginWithGoogle(idToken);
            saveSession(data);

            toast.success("Google login successful. Opening workspace...");
            redirectAfterLogin(data);
        } catch (error) {
            toast.error(getErrorMessage(error, "Google login failed. Please try again."));
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error("Google login failed. Please try again.");
    };

    return (
        <main className="auth-page">
            <div className="auth-preview-bg" aria-hidden="true">
                <div className="auth-topbar">
                    <div className="auth-mini-brand">
                        <div className="auth-mini-logo">
                            <Sparkles size={18} />
                        </div>
                        <div>
                            <strong>SHRIANSH NEXUS</strong>
                            <span>AI Growth CMS</span>
                        </div>
                    </div>

                    <div className="auth-top-actions">
                        <span>EN</span>
                        <span>Admin</span>
                    </div>
                </div>

                <div className="auth-site-wall">
                    <div className="auth-site-card realestate">
                        <div className="site-nav">RealEstate <span>Home · Properties · Contact</span></div>
                        <h3>Discover Your Dream Property</h3>
                        <p>Premium homes, apartments, and verified property enquiries.</p>
                        <button>Search</button>
                    </div>

                    <div className="auth-site-card fitness">
                        <div className="site-nav">Fitness <span>Programs · Trainers · Pricing</span></div>
                        <h3>Transform Your Body</h3>
                        <p>Memberships, trainers, and class bookings in one website.</p>
                        <button>Join Now</button>
                    </div>

                    <div className="auth-site-card healthcare">
                        <div className="site-nav">CurePlus <span>Doctors · Services · Contact</span></div>
                        <h3>Your Health, Our Priority</h3>
                        <p>Clinic websites with doctors, services, appointments, and trust.</p>
                        <button>Book Appointment</button>
                    </div>

                    <div className="auth-site-card ecommerce">
                        <div className="site-nav">Veloura <span>Shop · Categories · Cart</span></div>
                        <h3>New Collection</h3>
                        <p>Product catalog, detail pages, and conversion focused shopping.</p>
                        <button>Shop Now</button>
                    </div>

                    <div className="auth-site-card academy">
                        <div className="site-nav">Elite Academy <span>Programs · Coaches · Admissions</span></div>
                        <h3>Build Skills. Build Champions.</h3>
                        <p>Academy websites with programs, coach profiles, and admissions.</p>
                        <button>Explore Programs</button>
                    </div>

                    <div className="auth-site-card restaurant">
                        <div className="site-nav">Harvest Table <span>Menu · Gallery · Contact</span></div>
                        <h3>Exquisite Dining Experience</h3>
                        <p>Restaurant websites with menu, gallery, and booking intent.</p>
                        <button>View Menu</button>
                    </div>

                    <div className="auth-site-card legal">
                        <div className="site-nav">LexCore <span>Practice Areas · About · Contact</span></div>
                        <h3>Your Trusted Legal Partner</h3>
                        <p>Legal websites with service authority and consultation flow.</p>
                        <button>Schedule Consultation</button>
                    </div>

                    <div className="auth-site-card beauty">
                        <div className="site-nav">Glamour Studio <span>Services · Gallery · Contact</span></div>
                        <h3>Beauty That Inspires Confidence</h3>
                        <p>Salon and spa websites with services, team, and booking CTAs.</p>
                        <button>Book Now</button>
                    </div>
                </div>

                <div className="auth-bg-vignette" />
            </div>

            <section className="auth-center-shell">
                <form className="auth-card auth-card-light" onSubmit={handleSubmit}>
                    <div className="auth-card-brand">
                        <div className="auth-logo-mark">
                            <Sparkles size={22} />
                        </div>
                        <div>
                            <strong>SHRIANSH NEXUS</strong>
                            <span>{pageCopy.eyebrow}</span>
                        </div>
                    </div>

                    <h1 className="auth-card-title">{pageCopy.title}</h1>
                    <p className="auth-card-subtitle">{pageCopy.subtitle}</p>

                    <div className="auth-google-wrap">
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            text="continue_with"
                            shape="pill"
                            width="320"
                        />
                    </div>

                    {googleLoading && (
                        <div className="auth-inline-status">
                            Connecting Google account...
                        </div>
                    )}

                    <div className="auth-divider">
                        <span>or continue with email</span>
                    </div>

                    <div className="auth-field">
                        <label>Email address</label>
                        <div className="auth-input-icon-wrap">
                            <Mail size={18} />
                            <input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                type="email"
                                autoComplete="email"
                                required
                            />
                        </div>
                    </div>

                    <div className="auth-field">
                        <label>Password</label>
                        <div className="auth-input-icon-wrap auth-password-wrap">
                            <LockKeyhole size={18} />
                            <input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((value) => !value)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="auth-links-row">
                        <Link to="/forgot-password">Forgot password?</Link>
                    </div>

                    <button
                        type="submit"
                        className="auth-btn auth-btn-primary"
                        disabled={loading || googleLoading}
                    >
                        {loading ? "Signing in..." : "Sign in to dashboard"}
                        {!loading && <ArrowRight size={17} />}
                    </button>

                    <div className="auth-after-action">
                        <ShieldCheck size={17} />
                        <div>
                            <strong>{pageCopy.afterTitle}</strong>
                            <span>{pageCopy.afterText}</span>
                        </div>
                    </div>

                    <div className="auth-footer-text">
                        Don&apos;t have an account?{" "}
                        <Link
                            to={`/signup?entry=public${returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : ""}`}
                        >
                            Create account
                        </Link>
                    </div>
                </form>
            </section>
        </main>
    );
}